import LanguagePack from "../../../../../lib/html5-software-library/src/ranj/language/LanguagePack";
import RevisableLanguagePack from "../../../../../lib/html5-software-library/src/ranj/language/RevisableLanguagePack";
import LayoutManager from "../../../../../lib/html5-software-library/src/ranj/layout/LayoutManager";
import AbstractMediator from "../../../core/ui/AbstractMediator";
import TextTypeWriterAnimation from "../../../core/ui/animation/typewriters/TextTypeWriterAnimation";
import ClickEaterFactory from "../../../core/ui/ClickEaterFactory";
import SkinKeyRegistry from "../../../core/ui/SkinKeyRegistry";
import HubKeyDecisionListMediator from "../../hub/view/HubKeyDecisionListMediator";
import StoryVariables from "../../util/StoryVariables";
import FeedbackEvent from "../event/FeedbackEvent";
import FeedbackAnswersMediator from "./FeedbackAnswersMediator";

export default class FeedbackScreenMediator extends AbstractMediator {
    /**
     * @constructor
     * @param {createjs.MovieClip} target 
     * @param {soma.EventDispatcher} dispatcher 
     * @param {LayoutManager} layoutManager 
     * @param {ClickEaterFactory} clickEaterFactory 
     * @param {RevisableLanguagePack} languagePack 
     * 
     */
    constructor(target, dispatcher, layoutManager, clickEaterFactory, languagePack, mediatorManager, keyDecisionsModel, variableManager) {
        super(target, dispatcher, layoutManager, clickEaterFactory);
        this._variableManager = variableManager;
        this._languagePack = languagePack;
        this._mediatorManager = mediatorManager;
        this._keyDecisionsModel = keyDecisionsModel;
        this._answersViews = [];
        this._currentKeyDecision = 0;
        this._currentChapter = 0;
    }

    initialize() {
        super.initialize();
        this.preventClickThrough();

        this.registerEventListener(FeedbackEvent.MOVE_TO_KD_REQUEST, this._handleChangeKeyDecision.bind(this));

        this._currentChapter = this._variableManager.getVariable(StoryVariables.CHAPTER);

        this._target.FeedbackTitle.text = this._languagePack.getText(`feedback.general.title`);
        this._target.FeedbackDisclaimer.text = this._languagePack.getText(`feedback.general.disclaimer`);

        this._target.FeedbackKDdescription.text = "";

        /** @type {HubKeyDecisionListMediator} */
        this._keyDecisionListMediator = this._mediatorManager.createMediator(this._target.hubTimeline.keyDecisionsContainer, SkinKeyRegistry.HUB_KEY_DECISION_LIST_SKIN);
        this._keyDecisionListMediator.enableFeedbackMode();
        this._keyDecisionListMediator.initializeKeyDecisions(this._keyDecisionsModel.completedKeyDecisions.length);
        for (let completedKD of this._keyDecisionsModel.completedKeyDecisions) {
            this._keyDecisionListMediator.makeKeyDecisionAvailableForFeedback(completedKD);
        }
        this._keyDecisionListMediator.makeFirstKeyDecisionSelected();

        /** @type {FeedbackAnswersMediator} */
        this._answersViews.push(this._mediatorManager.createMediator(this._target.FeedbackWhiteScreen, SkinKeyRegistry.FEEDBACK_ANSWERS_SKIN));
        this._answersViews.push(this._mediatorManager.createMediator(this._target.FeedbackBlackScreen, SkinKeyRegistry.FEEDBACK_ANSWERS_SKIN));

        this._showOptimalButton = this.createButton(this._target.FeedbackToggle.ShowOptimal, this._handleShowOptimalButton.bind(this), this._languagePack.getText(`feedback.general.showOptimalButtonLabel`));
        this._hideOptimalButton = this.createButton(this._target.FeedbackToggle.HideOptimal, this._handleHideOptimalButton.bind(this), this._languagePack.getText(`feedback.general.hideOptimalButtonLabel`));


        this._previousButton = this.createButton(this._target.FeedbackPrevious, this._handlePreviousButton.bind(this), this._languagePack.getText(`feedback.general.previousButtonLabel`));
        this._nextButton = this.createButton(this._target.FeedbackNext, this._handleNextButton.bind(this), this._languagePack.getText(`feedback.general.nextButtonLabel`));

        this._answersViews[0].setPopupSkin(`FeedbackTooltipBodyWhite`);
        this._answersViews[1].setPopupSkin(`FeedbackTooltipBodyWhite`);

        this._answersViews[0].initAnswers();
        this._answersViews[1].initAnswers();

        this._refreshButtons();
        this._refreshAnswers();
        this._initViews();
    }

    _handleChangeKeyDecision(e) {
        var indexKD = e.params.data;
        if (indexKD != this._currentKeyDecision) {
            this._keyDecisionListMediator.selectKeyDecision(indexKD);
            let yIndex = (indexKD > this._currentKeyDecision ? 20 : -20);
            this.disableInteraction();
            this.hide(0, 500, yIndex)
                .then(() => {
                    this._currentKeyDecision = indexKD;
                    this._refreshButtons();
                    this._refreshAnswers();
                    this._initViews();
                    this.show(0, 500, -yIndex);
                })
                .then(() => {
                    this.enableInteraction();
                });
        }
    }

    destroy() {
        this._answersViews[0].destroy();
        this._answersViews[1].destroy();
        this._answersViews = null;
        super.destroy();
    }

    _initViews() {
        this._answersViews[0].show(0, 0);
        this._answersViews[1].hide(0, 0);
        this._target.FeedbackToggle.gotoAndStop(0);
    }

    _refreshButtons() {
        if (this._currentKeyDecision == this._keyDecisionsModel.completedKeyDecisions.length - 1) {
            this._nextButton.setLabel(this._languagePack.getText(`feedback.general.finishButtonLabel`));
        } else {
            this._nextButton.setLabel(this._languagePack.getText(`feedback.general.nextButtonLabel`));
        }
        this._previousButton.setEnabled(this._currentKeyDecision > 0);
    }

    _refreshAnswers() {
        if (this._currentKeyDecision in this._keyDecisionsModel.completedKeyDecisions) {
            let keyDecision = this._keyDecisionsModel.completedKeyDecisions[this._currentKeyDecision];
            let playerAnswers = keyDecision.playerAnswers;
            let optimalAnswers = keyDecision.optimalAnswers;
            this._answersViews[0].hideAnswers();
            this._answersViews[0].setAnswers(playerAnswers);
            this._answersViews[1].hideAnswers();
            this._answersViews[1].setAnswers(optimalAnswers);
            this._target.FeedbackKDdescription.text = this._languagePack.getText(keyDecision.descriptionLpKey);
        }
    }

    _handleShowOptimalButton() {
        this.disableInteraction();
        this._currentViewIndex = 0;
        this._target.FeedbackToggle.gotoAndStop(1);
        this._answersViews[0]
            .hide(0, 500)
            .then(() => { this._answersViews[1].show(0, 500) })
            .then(() => {
                this.enableInteraction();
            });
    }

    _handleHideOptimalButton() {
        this.disableInteraction();
        this._currentViewIndex = 1;
        this._target.FeedbackToggle.gotoAndStop(0);
        this._answersViews[1]
            .hide(0, 500)
            .then(() => { this._answersViews[0].show(0, 500) })
            .then(() => {
                this.enableInteraction();
            });
    }

    _handlePreviousButton() {
        this.disableInteraction();
        this._keyDecisionListMediator.selectKeyDecision(this._currentKeyDecision - 1);
        this.hide(0, 500, -20)
            .then(() => {
                --this._currentKeyDecision;
                this._refreshButtons();
                this._refreshAnswers();
                this._initViews();
                this.show(0, 500, +20);
            })
            .then(() => {
                this.enableInteraction();
            });
    }


    _handleNextButton() {
        if (this._currentKeyDecision < this._keyDecisionsModel.completedKeyDecisions.length - 1) {
            this.disableInteraction();
            this._keyDecisionListMediator.selectKeyDecision(this._currentKeyDecision + 1);
            this.hide(0, 500, 20)
                .then(() => {
                    ++this._currentKeyDecision;
                    this._refreshButtons();
                    this._refreshAnswers();
                    this._initViews();
                    this.show(0, 500, -20);
                })
                .then(() => {
                    this.enableInteraction();
                });
        } else {
            this.dispatch(FeedbackEvent.CLOSE_SCREEN_REQUEST);
        }
    }

    hide(wait = 0, animationTime = 750, offsetY = 20) {
        return new Promise((resolve) => {
            let initYFeedbackKDdescription = this._target.FeedbackKDdescription.y;
            let initYFeedbackWhiteScreen = this._target.FeedbackWhiteScreen.y;
            let initYFeedbackBlackScreen = this._target.FeedbackBlackScreen.y;
            createjs.Tween.get(this._target.FeedbackKDdescription).wait(wait).to({ y: initYFeedbackKDdescription + offsetY, alpha: 0 }, animationTime, createjs.Ease.sineOut);
            createjs.Tween.get(this._target.FeedbackWhiteScreen).wait(wait).to({ y: initYFeedbackWhiteScreen + offsetY, alpha: 0 }, animationTime, createjs.Ease.sineOut);
            createjs.Tween.get(this._target.FeedbackBlackScreen).wait(wait).to({ y: initYFeedbackBlackScreen + offsetY, alpha: 0 }, animationTime, createjs.Ease.sineOut)
                .call(() => {
                    this._target.FeedbackKDdescription.y -= offsetY;
                    this._target.FeedbackWhiteScreen.y -= offsetY;
                    this._target.FeedbackBlackScreen.y -= offsetY;
                    resolve();
                });
        });
    }

    show(wait = 0, animationTime = 750, offsetY = 20, previousOffsetY) {

        let initYFeedbackKDdescription = this._target.FeedbackKDdescription.y;
        let initYFeedbackWhiteScreen = this._target.FeedbackWhiteScreen.y;

        this._target.FeedbackKDdescription.y += offsetY;
        this._target.FeedbackWhiteScreen.y += offsetY;

        return new Promise((resolve) => {
            createjs.Tween.get(this._target.FeedbackKDdescription).wait(wait).to({ y: initYFeedbackKDdescription, alpha: 1 }, animationTime, createjs.Ease.sineOut);
            createjs.Tween.get(this._target.FeedbackWhiteScreen).wait(wait).to({ y: initYFeedbackWhiteScreen, alpha: 1 }, animationTime, createjs.Ease.sineOut).call(() => { resolve() });
        });
    }
}